import { useState } from 'react';
import { HelpCircle, ChevronDown, ExternalLink } from 'lucide-react';

const faqs = [
  {
    question: 'How does the program work?',
    answer: `The (approx) 14 week program is divided into 4 phases. Each phase takes between 3-4 weeks and consists of a warmup, 2 workouts, optional exercises, and a test out.

Each week, the athlete will train 3 days, completing Training Session I on one day, Training Session II on another day, and Optional Exercises on a third day. The athlete will document workout sets, reps, and other relevant data as they go. At the end of each phase, the athlete will complete and document Test Out exercises to move on to the next phase.`,
  },
  {
    question: 'How do the training sessions work and how do I complete them?',
    answer: `To complete a session, navigate to the appropriate phase from the "Program" tab, and then select "Training Sessions".

Each session consists of warmup and 4 supersets. Clicking a session will display instructions, the list of warmup exercises, and a list of supersets to complete. To start a session, click the start button, and start the warmup.

Within each superset, there are two exercises. Each exercise has a video and instructions. There is a floating button that allows the athlete to document stats for each set. Stats for one set can be applied to the next set(s) using the button.

When all supersets are complete, a summary screen will appear, and the athlete can click "Done" to complete the session.`,
  },
  {
    question: 'How does the Test Out process work?',
    answer: `At the end of each phase, the athlete will be required to complete a series of exercises designed to test progress. Each of these exercises involves comparing the strength, agility, etc. of the injured leg to the uninjured leg. This means that each exercise will be completed at least twice: once on the right side, and once on the left side.

The Test Out Documentation process is the same as the workouts. Clicking Test Out will display instructions, a stopwatch, and a list of exercises to complete. To start the Test Out, click play on the stopwatch, and then click into the first superset. There is a floating button that allows the athlete to document stats for each set. It is important to submit data for each set to ensure accurate results. When the athlete is finished, he or she will Submit the Test Out attempt using the button in the top right.`,
  },
  {
    question: 'I had pain after performing one of the workouts. Is this normal?',
    answer: `Muscle soreness is normal; pain is NOT normal for any of these workouts. If you are having pain, it could be a sign you are not ready to perform certain activities or may be performing a movement incorrectly. We recommend reaching out to your physical therapist or surgeon if these symptoms persist.`,
  },
  {
    question: "What if I don't have access to all of the equipment?",
    answer: `The required workouts are designed such that most exercises can be completed with minimal or no equipment. Feel free to get creative with adding weight by holding household items and using sturdy furniture to stand on for other exercises. Agility ladders are used in all workouts so we highly recommend this one purchase to complete the program in its entirety.`,
  },
  {
    question: 'If I fail a "test out," does that mean I am behind?',
    answer: `Not at all. This program is meant to challenge you and raise your abilities to a higher standard. Depending on your past exercise experience, it may take more practice to achieve these goals. We expect many users to fail at least one test out because sometimes it takes longer than 3 or 4 weeks to build appropriate levels of strength and power before moving on to the next phase. Trust the process and know the added time is helping push you to greater heights.`,
  },
  {
    question: 'I think I passed the "test out," but the next phase did not unlock. What\'s going on?',
    answer: `First, make sure you logged your data correctly. The testout calculation requires an entry for each leg, and both need to be on the same level. For example, if you were able to do Level 3 with your uninvolved leg, make sure you log stats for Level 3 on your involved leg, too. If it looks like everything was put in correctly, reach out to your PT (or the ACL Care Pro team) to get a code to move on to the next phase.`,
  },
  {
    question: 'Why are some workouts "optional"?',
    answer: `Not everyone has access to gym equipment, so we don't want that to limit anyone from making progress. That's why the gym-specific training day is marked as "optional" and the rest of the program is created to assure continued progress even without gym access. That said, the optional training day is highly recommended. Even if you can't get to a gym, we still recommend performing upper body exercises and cardio on this day to keep building your capacity.`,
  },
  {
    question: 'What if I don\'t document all of my "workout" and "test out" information?',
    answer: `The documentation of the "workout" is mostly for your own information. It's good to know what exactly you did during your last workout so you can aim to beat it on your next one. Conversely, the "test out" documentation is required to move forward onto the next phase. The test out documentation is also the information that is used for your final "test out report," which can be used to show your PT, surgeon, and/or coaches what you have achieved and your readiness to return to full activity.`,
  },
  {
    question: 'Why are some of the "test out" exercises repeated from phase to phase?',
    answer: `Some exercises are repeated because they serve as key benchmarks throughout your recovery journey. By testing the same movements at different phases, you can clearly see your progress over time. The passing thresholds increase as you advance through the phases, ensuring you're building toward full recovery and return to sport.`,
  },
  {
    question: 'My physical therapist, trainer/coach, or surgeon wants to know more information about this program. Is there any information I could send them?',
    answer: `Absolutely! First, we recommend providing them with the website link: www.aclcarepro.com. Additionally, send them the link to the Introduction video, which explains what this program is all about: https://www.youtube.com/watch?v=NVeem73DFyM. If they have any direct questions, please refer them to us!`,
  },
];

function FaqItem({ faq, isOpen, onToggle }) {
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full py-4 px-4 flex items-start justify-between text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-medium text-gray-900 pr-4">{faq.question}</span>
        <ChevronDown 
          className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      
      {isOpen && (
        <div className="px-4 pb-4">
          <div className="text-gray-600 whitespace-pre-line leading-relaxed">
            {faq.answer}
          </div>
          
          {/* Render links if present in answer */}
          {faq.answer.includes('www.aclcarepro.com') && (
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href="https://www.aclcarepro.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                aclcarepro.com
              </a>
              <a
                href="https://www.youtube.com/watch?v=NVeem73DFyM"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Introduction Video
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function FaqPage() {
  const [openIndex, setOpenIndex] = useState(null);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Group FAQs by category
  const programFaqs = faqs.slice(0, 3); // Program basics
  const workoutFaqs = faqs.slice(3, 9); // Workouts and test outs
  const otherFaqs = faqs.slice(9); // Other questions

  return (
    <div className="max-w-4xl mx-auto p-4 pb-24 lg:pb-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-2">
          <HelpCircle className="h-7 w-7 text-primary mr-2" />
          <h1 className="text-3xl font-bold text-gray-900">FAQs</h1>
        </div>
        <p className="text-gray-600">Find answers to commonly asked questions</p>
      </div>

      {/* Program Basics */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 px-1">
          Program Basics
        </h2>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {programFaqs.map((faq, index) => (
            <FaqItem
              key={index}
              faq={faq}
              isOpen={openIndex === index}
              onToggle={() => handleToggle(index)}
            />
          ))}
        </div>
      </section>

      {/* Workouts & Test Outs */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 px-1">
          Workouts & Test Outs
        </h2>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {workoutFaqs.map((faq, index) => (
            <FaqItem
              key={index + 3}
              faq={faq}
              isOpen={openIndex === index + 3}
              onToggle={() => handleToggle(index + 3)}
            />
          ))}
        </div>
      </section>

      {/* Other Questions */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 px-1">
          Other Questions
        </h2>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {otherFaqs.map((faq, index) => (
            <FaqItem
              key={index + 9}
              faq={faq}
              isOpen={openIndex === index + 9}
              onToggle={() => handleToggle(index + 9)}
            />
          ))}
        </div>
      </section>

      {/* Still Have Questions */}
      <section>
        <div className="bg-primary/5 rounded-xl border border-primary/20 p-6 text-center">
          <h3 className="font-semibold text-gray-900 mb-2">Still have questions?</h3>
          <p className="text-gray-600 mb-4">
            We're here to help. Reach out to the ACL Care Pro team for assistance.
          </p>
          <a
            href="mailto:info@aclcarepro.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors"
          >
            Contact Us
          </a>
        </div>
      </section>
    </div>
  );
}